import React, { useState, useMemo } from 'react';
import { ExternalLink, FileText, File, Image, FileSpreadsheet, FileVideo, FileAudio, Archive, RotateCcw, Trash2, Edit, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { createPageUrl } from '@/utils';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const getFileIcon = (mimeType) => {
  if (!mimeType) return <File className="w-4 h-4" />;

  if (mimeType.startsWith('image/')) return <Image className="w-4 h-4 text-blue-400" />;
  if (mimeType.startsWith('video/')) return <FileVideo className="w-4 h-4 text-purple-400" />;
  if (mimeType.startsWith('audio/')) return <FileAudio className="w-4 h-4 text-pink-400" />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <FileSpreadsheet className="w-4 h-4 text-green-400" />;
  if (mimeType.includes('document') || mimeType.includes('word')) return <FileText className="w-4 h-4 text-cyan-400" />;

  return <File className="w-4 h-4 text-slate-400" />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export default function DocumentTable({ documents, showContext = true }) {
  const queryClient = useQueryClient();
  const [editDialog, setEditDialog] = useState({ open: false, doc: null });
  const [editForm, setEditForm] = useState({ name: '', context_type: '' });
  const [sortField, setSortField] = useState(null); // 'date' or 'type'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'date' ? 'desc' : 'asc');
    }
  };

  const sortedDocuments = useMemo(() => {
    if (!sortField || !documents) return documents;
    return [...documents].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'date') {
        cmp = new Date(a.created_date) - new Date(b.created_date);
      } else if (sortField === 'type') {
        cmp = (a.file_type || '').localeCompare(b.file_type || '');
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [documents, sortField, sortDirection]);

  const archiveDocumentMutation = useMutation({
    mutationFn: (id) => base44.entities.Document.update(id, { status: 'archived' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] })
  });

  const unarchiveDocumentMutation = useMutation({
    mutationFn: (id) => base44.entities.Document.update(id, { status: 'active' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] })
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (id) => base44.entities.Document.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] })
  });

  const updateDocumentMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Document.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setEditDialog({ open: false, doc: null });
    }
  });

  const handleDelete = async (doc) => {
    if (confirm(`Delete "${doc.name}" permanently? This action cannot be undone.`)) {
      // If this document belongs to an Artifact, also clean up version history entries
      if (doc.context_type === 'Artifact' && doc.parent_context_id && doc.drive_web_link) {
        const allVersions = await base44.entities.ArtifactVersion.list();
        const matchingVersions = allVersions.filter(
          v => v.artifact_id === doc.parent_context_id && v.file_url === doc.drive_web_link
        );
        for (const version of matchingVersions) {
          await base44.entities.ArtifactVersion.delete(version.id);
        }
        queryClient.invalidateQueries({ queryKey: ['artifactVersions'] });
      }
      deleteDocumentMutation.mutate(doc.id);
    }
  };

  const handleEdit = (doc) => {
    setEditForm({ name: doc.name, context_type: doc.context_type });
    setEditDialog({ open: true, doc });
  };

  const handleSaveEdit = () => {
    if (!editForm.name.trim()) return;
    updateDocumentMutation.mutate({
      id: editDialog.doc.id,
      data: {
        name: editForm.name,
        context_type: editForm.context_type
      }
    });
  };

  if (!documents || documents.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        No documents uploaded yet
      </div>);

  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => handleSort('type')}>
              <div className="flex items-center gap-1">
                File
                {sortField === 'type' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
              </div>
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase">Name</th>
            {showContext &&
            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase">Context</th>
            }
            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase">Size</th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase">Uploaded By</th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => handleSort('date')}>
              <div className="flex items-center gap-1">
                Date
                {sortField === 'date' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
              </div>
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedDocuments.map((doc) =>
          <tr key={doc.id} className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors">
              <td className="py-3 px-4">
                {getFileIcon(doc.file_type)}
              </td>
              <td className="py-3 px-4">
                <div className="flex flex-col">
                  <span className="text-slate-600 text-sm">{doc.name}</span>
                  <span className="text-xs text-slate-500">{doc.file_type}</span>
                </div>
              </td>
              {showContext &&
            <td className="py-3 px-4">
                  {doc.context_type === 'General' ?
              <Badge variant="outline" className="text-slate-400 border-slate-600">
                      General
                    </Badge> :
              doc.context_type === 'Task' ?
              <Link
                to={createPageUrl(`dailytasks?taskId=${doc.parent_context_id}`)}
                className="text-sm text-indigo-400 hover:text-indigo-300 underline">

                      Task
                    </Link> :

              <Link
                to={createPageUrl(`artifacts?artifactId=${doc.parent_context_id}`)}
                className="text-sm text-cyan-400 hover:text-cyan-300 underline">

                      Artifact
                    </Link>
              }
                </td>
            }
              <td className="py-3 px-4 text-sm text-slate-400">
                {formatFileSize(doc.file_size)}
              </td>
              <td className="py-3 px-4 text-sm text-slate-400">
                {doc.uploaded_by}
              </td>
              <td className="py-3 px-4 text-sm text-slate-400">
                <div className="flex flex-col">
                  <span>{format(new Date(doc.created_date), 'MMM d, yyyy')}</span>
                  <span className="text-xs text-slate-500">{format(new Date(doc.created_date), 'h:mm a')}</span>
                </div>
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <a
                  href={doc.drive_web_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300">

                    <ExternalLink className="w-4 h-4" />
                    Open
                  </a>
                  {doc.status === 'archived' ? (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => unarchiveDocumentMutation.mutate(doc.id)}
                        className="h-7 w-7 text-slate-500 hover:text-emerald-400 hover:bg-slate-800"
                        title="Unarchive">
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(doc)}
                        className="h-7 w-7 text-slate-500 hover:text-rose-400 hover:bg-slate-800"
                        title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEdit(doc)}
                        className="h-7 w-7 text-slate-500 hover:text-blue-400 hover:bg-slate-800"
                        title="Edit">
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => archiveDocumentMutation.mutate(doc.id)}
                        className="h-7 w-7 text-slate-500 hover:text-amber-400 hover:bg-slate-800"
                        title="Archive">
                        <Archive className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(doc)}
                        className="h-7 w-7 text-slate-500 hover:text-rose-400 hover:bg-slate-800"
                        title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, doc: null })}>
        <DialogContent data-testid="doclib-documenttable-dialog" className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Edit Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300">Document Name</Label>
              <Input data-testid="doclib-text-slate-300-input"
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-slate-800 border-slate-700 text-slate-100"
                placeholder="Enter document name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="context" className="text-slate-300">Context Type</Label>
              <Select value={editForm.context_type} onValueChange={(val) => setEditForm({ ...editForm, context_type: val })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General</SelectItem>
                  <SelectItem value="Task">Task</SelectItem>
                  <SelectItem value="Artifact">Artifact</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="doclib-cancel-btn" variant="outline" onClick={() => setEditDialog({ open: false, doc: null })} className="border-slate-700">
              Cancel
            </Button>
            <Button data-testid="doclib-save-changes-btn" onClick={handleSaveEdit} className="bg-indigo-600 hover:bg-indigo-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

}